import React, { CSSProperties, useEffect, useState } from "react";

type CardFlipProps = {
  cardZIndex?: CSSProperties["zIndex"];
  isFlipped?: boolean;
  flipSpeedBackToFront?: number;
  flipSpeedFrontToBack?: number;
  infinite?: boolean;
  flipDirection?: "horizontal" | "vertical";
  children: [React.ReactNode, React.ReactNode];
};

export const CardFlip = ({
  cardZIndex = "auto",
  flipDirection = "vertical",
  flipSpeedFrontToBack = 500,
  flipSpeedBackToFront = 500,
  infinite = false,
  isFlipped = false,
  children,
}: CardFlipProps): React.ReactElement => {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    setRotation((c) => c + 180);
  }, [isFlipped]);

  const getComponent = (key: 0 | 1): React.ReactNode => {
    if (children.length !== 2) {
      throw new Error("Component ReactCardFlip requires 2 children");
    }

    return children[key];
  };

  const frontRotateY = `rotateY(${
    infinite ? rotation : isFlipped ? 180 : 0
  }deg)`;
  const backRotateY = `rotateY(${
    infinite ? rotation + 180 : isFlipped ? 0 : -180
  }deg)`;
  const frontRotateX = `rotateX(${
    infinite ? rotation : isFlipped ? 180 : 0
  }deg)`;
  const backRotateX = `rotateX(${
    infinite ? rotation + 180 : isFlipped ? 0 : -180
  }deg)`;

  const styles: Record<string, CSSProperties> = {
    back: {
      WebkitBackfaceVisibility: "hidden",
      backfaceVisibility: "hidden",
      height: "100%",
      left: "0",
      position: isFlipped ? "relative" : "absolute",
      top: "0",
      transform: flipDirection === "horizontal" ? backRotateY : backRotateX,
      transformStyle: "preserve-3d",
      transition: `${flipSpeedFrontToBack}ms`,
      width: "100%",
    },
    container: {
      perspective: "1000px",
      zIndex: cardZIndex,
    },
    flipper: {
      height: "100%",
      position: "relative",
      width: "100%",
      transformStyle: "preserve-3d",
    },
    front: {
      WebkitBackfaceVisibility: "hidden",
      backfaceVisibility: "hidden",
      height: "100%",
      left: "0",
      position: isFlipped ? "absolute" : "relative",
      top: "0",
      transform: flipDirection === "horizontal" ? frontRotateY : frontRotateX,
      transformStyle: "preserve-3d",
      transition: `${flipSpeedBackToFront}ms`,
      width: "100%",
      zIndex: 2,
    },
  };

  return (
    <section style={styles.container}>
      <section style={styles.flipper}>
        <section style={styles.front}>{getComponent(0)}</section>

        <section style={styles.back}>{getComponent(1)}</section>
      </section>
    </section>
  );
};
